import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Copy, Pencil, Pause, Play, Archive, History, Users as UsersIcon, Link2, Check, X, ChevronDown, ChevronRight } from "lucide-react";
import { getToken } from "../../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ Authorization: `Bearer ${getToken()}` });

export default function PartnersPanel() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [audit, setAudit] = useState(null);         // {partnerId, rows}
  const [members, setMembers] = useState(null);      // {partnerId, rows}

  const siteOrigin = "https://myae.co.uk";

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/partners`, { headers: authHdr() });
      setPartners(r.data?.partners || []);
    } catch {
      toast.error("Could not load partners.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const copyLink = (p) => {
    const url = `${siteOrigin}/r/${p.slug}`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success(`Copied ${url}`),
      () => toast.error("Copy failed."),
    );
  };

  const setStatus = async (p, next) => {
    const reason = window.prompt(`Reason for → ${next}?`, "");
    if (reason === null) return;
    try {
      await axios.post(`${API}/admin/partners/${p.id}/status`,
        { status: next, reason }, { headers: authHdr() });
      toast.success(`Status → ${next}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  const changeSlug = async (p) => {
    const next = window.prompt(
      `Change referral slug for "${p.label}".\n\n`
      + `Current: ${p.slug}\n\n`
      + `New slug (3–40 chars, lowercase a–z, 0–9, hyphens):`,
      p.slug,
    );
    if (next === null) return;
    const normalised = next.trim().toLowerCase();
    if (!normalised || normalised === p.slug) return;
    const reason = window.prompt(
      `Reason for direct slug change (visible in audit log):`,
      "direct admin slug change",
    );
    if (reason === null) return;
    try {
      await axios.post(`${API}/admin/partners/${p.id}/slug`,
        { new_slug: normalised, reason }, { headers: authHdr() });
      toast.success(`Slug → ${normalised}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Slug change failed."); }
  };

  const openAudit = async (p) => {
    setAudit({ partnerId: p.id, rows: [] });
    try {
      const r = await axios.get(`${API}/admin/partners/${p.id}/audit`,
        { headers: authHdr(), params: { limit: 100 } });
      setAudit({ partnerId: p.id, slug: p.slug, rows: r.data?.audit || [] });
    } catch { setAudit({ partnerId: p.id, slug: p.slug, rows: [] }); }
  };

  const openMembers = async (p) => {
    setMembers({ partnerId: p.id, slug: p.slug, label: p.label, rows: [] });
    try {
      const r = await axios.get(`${API}/admin/partners/${p.id}/memberships`, { headers: authHdr() });
      setMembers({ partnerId: p.id, slug: p.slug, label: p.label, rows: r.data?.memberships || [] });
    } catch { setMembers({ partnerId: p.id, slug: p.slug, label: p.label, rows: [] }); }
  };

  const total = useMemo(() => partners.length, [partners]);
  const activeCount = useMemo(() => partners.filter(p => p.status === "active").length, [partners]);

  if (loading) return <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="animate-spin" size={14} />Loading partners…</div>;

  return (
    <div data-testid="partners-panel" className="space-y-6">
      <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600">
        <strong>MyAE is a trading name of OpenDegree Ltd.</strong> Partners
        receive a configurable share of eligible <em>contribution profit</em> —
        never gross revenue. Each rate is editable per partner; no commission
        percentage is hard-coded in application logic.
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Tile label="Total partners" value={total} testid="tile-partners-total" />
        <Tile label="Active" value={activeCount} testid="tile-partners-active" />
        <Tile label="Attribution window" value="30 days (first-touch, direct URL overrides)" testid="tile-attribution-rule" />
      </div>

      <div className="flex justify-end">
        <button onClick={() => setCreating(true)}
          className="bg-indigo-600 text-white px-3 py-1.5 text-sm inline-flex items-center gap-1"
          data-testid="partner-create-btn"><Plus size={14} />Add partner</button>
      </div>

      <div className="overflow-x-auto border border-zinc-200">
        <table className="w-full text-sm" data-testid="partners-table">
          <thead className="bg-zinc-50 text-xs text-zinc-500 text-left">
            <tr>
              <th className="px-3 py-2">Slug</th>
              <th className="px-3 py-2">Label</th>
              <th className="px-3 py-2">Rate</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Referral link</th>
              <th className="px-3 py-2">Stats</th>
              <th className="px-3 py-2 w-56">Actions</th>
            </tr>
          </thead>
          <tbody>
            {partners.map((p) => (
              <tr key={p.id} data-testid={`partner-row-${p.slug}`} className="border-t border-zinc-100">
                <td className="px-3 py-2 font-mono text-xs">{p.slug}</td>
                <td className="px-3 py-2">{p.label}</td>
                <td className="px-3 py-2 font-mono">{p.commission_rate_pct}%</td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-2 py-0.5 text-[11px] rounded ${
                    p.status === "active" ? "bg-emerald-100 text-emerald-800"
                      : p.status === "suspended" ? "bg-amber-100 text-amber-800"
                      : "bg-zinc-200 text-zinc-600"
                  }`}>{p.status}</span>
                </td>
                <td className="px-3 py-2 text-xs">
                  <code className="text-indigo-700">{siteOrigin}/r/{p.slug}</code>
                </td>
                <td className="px-3 py-2 text-xs text-zinc-600" title="Distinct accounts attributed to this partner via a signed-in visit to their link. Admins and partner members are excluded. 'Paid' = attributed accounts with at least one paid payment transaction.">
                  {p.stats
                    ? `${p.stats.attributed_users} attributed · ${p.stats.attributed_paid} paid`
                    : "—"}
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 flex-wrap">
                    <button onClick={() => copyLink(p)} className="text-xs text-indigo-700 inline-flex items-center gap-0.5"
                      data-testid={`partner-copy-${p.slug}`}><Copy size={11} />Copy</button>
                    <button onClick={() => setEditing(p)} className="text-xs text-zinc-700 inline-flex items-center gap-0.5"
                      data-testid={`partner-edit-${p.slug}`}><Pencil size={11} />Edit</button>
                    <button onClick={() => changeSlug(p)} className="text-xs text-indigo-700 inline-flex items-center gap-0.5"
                      data-testid={`partner-change-slug-${p.slug}`}><Link2 size={11} />Change slug</button>
                    {p.status === "active" ? (
                      <button onClick={() => setStatus(p, "suspended")} className="text-xs text-amber-700 inline-flex items-center gap-0.5"
                        data-testid={`partner-suspend-${p.slug}`}><Pause size={11} />Suspend</button>
                    ) : p.status === "suspended" ? (
                      <button onClick={() => setStatus(p, "active")} className="text-xs text-emerald-700 inline-flex items-center gap-0.5"
                        data-testid={`partner-resume-${p.slug}`}><Play size={11} />Resume</button>
                    ) : null}
                    {p.status !== "archived" && (
                      <button onClick={() => setStatus(p, "archived")} className="text-xs text-rose-700 inline-flex items-center gap-0.5"
                        data-testid={`partner-archive-${p.slug}`}><Archive size={11} />Archive</button>
                    )}
                    <button onClick={() => openMembers(p)} className="text-xs text-zinc-700 inline-flex items-center gap-0.5"
                      data-testid={`partner-members-${p.slug}`}><UsersIcon size={11} />Members</button>
                    <button onClick={() => openAudit(p)} className="text-xs text-zinc-600 inline-flex items-center gap-0.5"
                      data-testid={`partner-audit-${p.slug}`}><History size={11} />Audit</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <CreatePartnerModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      {editing && <EditPartnerModal partner={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {audit && <AuditModal data={audit} onClose={() => setAudit(null)} />}
      {members && <MembersModal data={members} onClose={() => setMembers(null)} onChanged={() => openMembers({ id: members.partnerId, slug: members.slug, label: members.label })} />}

      <SlugRequestsSection onChanged={load} />
    </div>
  );
}

const Tile = ({ label, value, testid }) => (
  <div className="rounded-md border border-zinc-200 bg-white p-3" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
    <p className="font-mono text-sm mt-1">{value}</p>
  </div>
);

const CreatePartnerModal = ({ onClose, onCreated }) => {
  const [slug, setSlug] = useState("");
  const [label, setLabel] = useState("");
  const [rate, setRate] = useState("50");
  const [agreementUrl, setAgreementUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!slug.trim() || !label.trim()) return toast.error("Slug and label are required.");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/partners`,
        { slug: slug.trim(), label: label.trim(), commission_rate_pct: rate,
          agreement_url: agreementUrl, agreement_notes: notes, reason: "created via admin" },
        { headers: authHdr() });
      toast.success("Partner created.");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed."); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Add partner" onClose={onClose}>
      <Field label="Slug (lowercase, letters/digits/hyphens, 3–40 chars)" value={slug} onChange={setSlug} testid="create-slug" placeholder="e.g. topedu" mono />
      <Field label="Label" value={label} onChange={setLabel} testid="create-label" placeholder="e.g. Top Education" />
      <Field label="Commission rate (%, 0–100, applied to contribution profit)" value={rate} onChange={setRate} testid="create-rate" mono />
      <Field label="Agreement URL (optional)" value={agreementUrl} onChange={setAgreementUrl} testid="create-agreement-url" />
      <Field label="Agreement notes (optional)" value={notes} onChange={setNotes} testid="create-notes" multiline />
      <div className="flex justify-end gap-2 mt-3">
        <button onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
        <button onClick={submit} disabled={busy} className="bg-indigo-600 text-white px-3 py-1.5 text-sm disabled:opacity-50" data-testid="create-save">
          {busy ? <Loader2 className="animate-spin inline" size={13} /> : "Create"}
        </button>
      </div>
    </Modal>
  );
};

const EditPartnerModal = ({ partner, onClose, onSaved }) => {
  const [label, setLabel] = useState(partner.label);
  const [rate, setRate] = useState(partner.commission_rate_pct);
  const [agreementUrl, setAgreementUrl] = useState(partner.agreement_url || "");
  const [notes, setNotes] = useState(partner.agreement_notes || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const changed = label !== partner.label || rate !== partner.commission_rate_pct
    || agreementUrl !== (partner.agreement_url || "") || notes !== (partner.agreement_notes || "");
  const rateChanged = rate !== partner.commission_rate_pct;
  const submit = async () => {
    if (!changed) { onClose(); return; }
    if (rateChanged && !reason.trim()) return toast.error("Reason is required when changing the commission rate.");
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/partners/${partner.id}`,
        { label, commission_rate_pct: rate, agreement_url: agreementUrl, agreement_notes: notes, reason },
        { headers: authHdr() });
      toast.success("Partner updated.");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Edit partner — ${partner.slug}`} onClose={onClose}>
      <Field label="Label" value={label} onChange={setLabel} testid="edit-label" />
      <Field label="Commission rate (%)" value={rate} onChange={setRate} testid="edit-rate" mono />
      {rateChanged && (
        <>
          <div className="rounded border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900 mb-2">
            Rate change: {partner.commission_rate_pct}% → {rate}%. Previously
            finalised commission entries are NOT recalculated (immutable).
          </div>
          <Field label="Reason (required)" value={reason} onChange={setReason} testid="edit-reason" />
        </>
      )}
      <Field label="Agreement URL" value={agreementUrl} onChange={setAgreementUrl} testid="edit-agreement-url" />
      <Field label="Agreement notes" value={notes} onChange={setNotes} testid="edit-notes" multiline />
      <div className="flex justify-end gap-2 mt-3">
        <button onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
        <button onClick={submit} disabled={busy || !changed}
          className="bg-indigo-600 text-white px-3 py-1.5 text-sm disabled:opacity-50"
          data-testid="edit-save">
          {busy ? <Loader2 className="animate-spin inline" size={13} /> : "Save"}
        </button>
      </div>
    </Modal>
  );
};

const AuditModal = ({ data, onClose }) => (
  <Modal title={`Audit — ${data.slug || data.partnerId}`} onClose={onClose}>
    <div className="max-h-96 overflow-y-auto text-xs" data-testid="audit-list">
      {(!data.rows || data.rows.length === 0) && <p className="text-zinc-500">No audit entries.</p>}
      {(data.rows || []).map((r) => (
        <div key={r.id} className="border-t py-2">
          <div className="flex justify-between">
            <span className="font-mono text-indigo-700">{r.type}</span>
            <span className="text-zinc-500">{new Date(r.created_at).toLocaleString()}</span>
          </div>
          <div className="text-zinc-600 mt-0.5">by {r.actor_email || r.actor_user_id}</div>
          {r.reason && <div className="text-zinc-700 italic">"{r.reason}"</div>}
          {r.before && <div className="font-mono text-[10px] text-rose-700 mt-1">before: {JSON.stringify(r.before)}</div>}
          {r.after && <div className="font-mono text-[10px] text-emerald-700">after: {JSON.stringify(r.after)}</div>}
        </div>
      ))}
    </div>
  </Modal>
);

const MembersModal = ({ data, onClose, onChanged }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("partner_admin");
  const [resolved, setResolved] = useState(undefined); // undefined=idle, null=not found, {user_id,email,name}=found
  const [lookingUp, setLookingUp] = useState(false);
  const [adding, setAdding] = useState(false);

  // Debounced server-side resolve on email change (300ms)
  useEffect(() => {
    const q = email.trim();
    if (!q || !q.includes("@")) { setResolved(undefined); return; }
    setLookingUp(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/admin/users/lookup`, {
          params: { email: q }, headers: authHdr(),
        });
        setResolved(r.data?.user || null);
      } catch { setResolved(null); }
      setLookingUp(false);
    }, 300);
    return () => clearTimeout(t);
  }, [email]);

  // Guard — is this user already a member of THIS partner?
  const existing = (data.rows || []).find(
    (m) => resolved && m.user_id === resolved.user_id,
  );

  const add = async () => {
    if (!resolved) return toast.error("Enter the email of a registered MyAE account.");
    if (existing && existing.status === "active") {
      return toast.error("That account is already an active member.");
    }
    setAdding(true);
    try {
      await axios.post(`${API}/admin/partners/${data.partnerId}/memberships`,
        { email: resolved.email, role }, { headers: authHdr() });
      toast.success(`${resolved.email} added as ${role}.`);
      setEmail(""); setResolved(undefined);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed.");
    }
    setAdding(false);
  };

  const revoke = async (m) => {
    if (!window.confirm(`Revoke partner access for ${m.email || m.user_id}?\n\nThis stops them seeing the partner portal but does NOT delete their MyAE student account.`)) return;
    try {
      await axios.post(`${API}/admin/partners/memberships/${m.id}/revoke`,
        { reason: "revoked via admin" }, { headers: authHdr() });
      toast.success("Membership revoked.");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed."); }
  };

  return (
    <Modal title={`Members — ${data.label}`} onClose={onClose}>
      <p className="text-xs text-zinc-600 mb-3">
        Add an existing MyAE account to this partner's team. Partner members can see the partner portal
        (coming in P-D) but do <strong>not</strong> get access to student assignments, grades, or any other partner's data.
        Server-side authorisation enforces this separately from ordinary student access.
      </p>

      <div className="mb-2 flex gap-2 items-end">
        <div className="flex-1">
          <label className="block text-xs text-zinc-600 mb-1">Registered email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
            className="w-full border px-2 py-1 text-sm" data-testid="member-add-email"
            placeholder="person@topeducation.co.uk" autoComplete="off" />
        </div>
        <div>
          <label className="block text-xs text-zinc-600 mb-1">Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value)}
            className="border px-2 py-1 text-sm" data-testid="member-add-role">
            <option value="partner_admin">partner_admin</option>
            <option value="viewer">viewer</option>
          </select>
        </div>
        <button onClick={add}
          disabled={!resolved || adding || (existing && existing.status === "active")}
          className="bg-indigo-600 text-white px-3 py-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="member-add-submit">
          {adding ? "Adding…" : "Add member"}
        </button>
      </div>

      {/* Live resolve preview — the heart of the UX fix */}
      <div className="mb-4 min-h-[36px]" data-testid="member-resolve">
        {lookingUp && <p className="text-xs text-zinc-500 italic">Looking up…</p>}
        {!lookingUp && resolved === null && email.trim() && (
          <p className="text-xs text-rose-700" data-testid="member-resolve-missing">
            No MyAE account exists with that email. The person must register first at{" "}
            <a href="/signup" className="underline" target="_blank" rel="noopener noreferrer">myae.co.uk/signup</a>.
          </p>
        )}
        {!lookingUp && resolved && (
          <div className="text-xs flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-2 py-1.5" data-testid="member-resolve-found">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
            <span>
              <strong>{resolved.name || "(no name)"}</strong>{" "}
              — <span className="font-mono text-zinc-700">{resolved.email}</span>
              {existing && existing.status === "active" && (
                <span className="ml-2 text-amber-800">· already an active {existing.role}</span>
              )}
              {existing && existing.status === "revoked" && (
                <span className="ml-2 text-zinc-600">· previously revoked — re-adding is allowed</span>
              )}
            </span>
          </div>
        )}
      </div>

      <table className="w-full text-xs" data-testid="members-table">
        <thead className="text-left text-zinc-500">
          <tr><th className="py-1">Member</th><th>Role</th><th>Status</th><th></th></tr>
        </thead>
        <tbody>
          {(data.rows || []).map((m) => (
            <tr key={m.id} className="border-t">
              <td className="py-1">
                <div>{m.email || <span className="font-mono text-zinc-500">{m.user_id}</span>}</div>
                {m.name && <div className="text-[11px] text-zinc-500">{m.name}</div>}
              </td>
              <td className="py-1">{m.role}</td>
              <td className="py-1">
                <span className={`px-1.5 py-0.5 ${m.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-200 text-zinc-600"}`}>{m.status}</span>
              </td>
              <td className="py-1 text-right">
                {m.status === "active" && (
                  <button onClick={() => revoke(m)} className="text-rose-700" data-testid={`member-revoke-${m.user_id}`}>Revoke</button>
                )}
              </td>
            </tr>
          ))}
          {(data.rows || []).length === 0 && <tr><td colSpan="4" className="py-2 text-zinc-500">No members yet.</td></tr>}
        </tbody>
      </table>
    </Modal>
  );
};

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white max-w-xl w-full p-5" onClick={(e) => e.stopPropagation()}>
      <h3 className="text-sm font-medium mb-3">{title}</h3>
      {children}
    </div>
  </div>
);

const Field = ({ label, value, onChange, testid, placeholder, multiline, mono }) => (
  <div className="mb-3">
    <label className="block text-xs text-zinc-600 mb-1">{label}</label>
    {multiline ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)}
        rows={2} className={`w-full border px-2 py-1 text-sm ${mono ? "font-mono" : ""}`}
        data-testid={testid} placeholder={placeholder} />
    ) : (
      <input value={value} onChange={(e) => onChange(e.target.value)}
        className={`w-full border px-2 py-1 text-sm ${mono ? "font-mono" : ""}`}
        data-testid={testid} placeholder={placeholder} />
    )}
  </div>
);

// ---------------------------------------------------------------------------
// P-G — Slug-change requests
// ---------------------------------------------------------------------------
const SlugRequestsSection = ({ onChanged }) => {
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [rejecting, setRejecting] = useState(null);     // request row being rejected
  const [approvingId, setApprovingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [pendingRes, approvedRes, rejectedRes] = await Promise.all([
        axios.get(`${API}/admin/partners/slug-requests`,
          { params: { status: "pending", limit: 200 }, headers: authHdr() }),
        axios.get(`${API}/admin/partners/slug-requests`,
          { params: { status: "approved", limit: 50 }, headers: authHdr() }),
        axios.get(`${API}/admin/partners/slug-requests`,
          { params: { status: "rejected", limit: 50 }, headers: authHdr() }),
      ]);
      setPending(pendingRes.data?.requests || []);
      const combined = [
        ...(approvedRes.data?.requests || []),
        ...(rejectedRes.data?.requests || []),
      ].sort((a, b) => (b.decided_at || "").localeCompare(a.decided_at || ""));
      setHistory(combined);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load slug requests.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const approve = async (r) => {
    const ok = window.confirm(
      `Approve slug change for "${r.partner_label || r.partner_id}"?\n\n`
      + `${r.partner_current_slug || r.current_slug} → ${r.proposed_slug}\n\n`
      + `This updates the canonical referral link immediately and retains the old slug `
      + `in history so existing links continue to attribute correctly.`
    );
    if (!ok) return;
    setApprovingId(r.id);
    try {
      await axios.post(`${API}/admin/partners/slug-requests/${r.id}/approve`,
        {}, { headers: authHdr() });
      toast.success("Slug change approved.");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed.");
    } finally { setApprovingId(null); }
  };

  return (
    <div className="space-y-4" data-testid="slug-requests-section">
      <div className="flex items-center gap-2 mt-6">
        <Link2 size={16} className="text-indigo-700" />
        <h3 className="text-sm font-semibold text-zinc-800">Referral-slug change requests</h3>
      </div>
      <p className="text-xs text-zinc-600 max-w-3xl">
        Partners cannot change their referral slug directly — admin approval is required.
        Approving a request atomically swaps the canonical slug, pushes the old slug to
        history (so existing referral links keep working) and writes an audit entry.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 className="animate-spin" size={14} /> Loading requests…
        </div>
      ) : (
        <>
          {/* Pending */}
          <div className="overflow-x-auto border border-zinc-200" data-testid="slug-requests-pending">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs text-zinc-500 text-left">
                <tr>
                  <th className="px-3 py-2">Partner</th>
                  <th className="px-3 py-2">Current slug</th>
                  <th className="px-3 py-2">Proposed slug</th>
                  <th className="px-3 py-2">Requested by</th>
                  <th className="px-3 py-2">Submitted</th>
                  <th className="px-3 py-2 w-40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pending.length === 0 && (
                  <tr><td colSpan="6" className="px-3 py-4 text-center text-zinc-500" data-testid="slug-requests-empty">
                    No pending slug-change requests.
                  </td></tr>
                )}
                {pending.map((r) => (
                  <tr key={r.id} className="border-t border-zinc-100"
                      data-testid={`slug-request-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div>{r.partner_label || "—"}</div>
                      {r.partner_status && r.partner_status !== "active" && (
                        <div className="text-[11px] text-amber-700">{r.partner_status}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.partner_current_slug || r.current_slug || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs text-indigo-700" data-testid="slug-request-proposed">{r.proposed_slug}</td>
                    <td className="px-3 py-2 text-xs text-zinc-700">{r.requested_by_email || "—"}</td>
                    <td className="px-3 py-2 text-xs text-zinc-600">{new Date(r.requested_at).toLocaleString()}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => approve(r)}
                          disabled={approvingId === r.id}
                          className="text-xs bg-emerald-600 text-white px-2 py-1 inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`slug-request-approve-${r.id}`}>
                          {approvingId === r.id ? <Loader2 className="animate-spin" size={11} /> : <Check size={11} />}
                          Approve
                        </button>
                        <button
                          onClick={() => setRejecting(r)}
                          className="text-xs bg-rose-600 text-white px-2 py-1 inline-flex items-center gap-1"
                          data-testid={`slug-request-reject-${r.id}`}>
                          <X size={11} /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* History (collapsed) */}
          <div className="border border-zinc-200" data-testid="slug-requests-history-wrap">
            <button
              onClick={() => setShowHistory((v) => !v)}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-zinc-700 bg-zinc-50 hover:bg-zinc-100"
              data-testid="slug-requests-history-toggle">
              {showHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              History ({history.length})
            </button>
            {showHistory && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="slug-requests-history-table">
                  <thead className="bg-zinc-50 text-xs text-zinc-500 text-left">
                    <tr>
                      <th className="px-3 py-2">Partner</th>
                      <th className="px-3 py-2">Change</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Decided by</th>
                      <th className="px-3 py-2">Decided</th>
                      <th className="px-3 py-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.length === 0 && (
                      <tr><td colSpan="6" className="px-3 py-4 text-center text-zinc-500">
                        No past slug-change decisions yet.
                      </td></tr>
                    )}
                    {history.map((r) => (
                      <tr key={r.id} className="border-t border-zinc-100"
                          data-testid={`slug-request-history-row-${r.id}`}>
                        <td className="px-3 py-2">{r.partner_label || "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {r.current_slug || "—"} → <span className="text-indigo-700">{r.proposed_slug}</span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 text-[11px] ${
                            r.status === "approved" ? "bg-emerald-100 text-emerald-800"
                              : "bg-rose-100 text-rose-800"
                          }`}>{r.status}</span>
                        </td>
                        <td className="px-3 py-2 text-xs text-zinc-700">{r.decided_by_email || "—"}</td>
                        <td className="px-3 py-2 text-xs text-zinc-600">
                          {r.decided_at ? new Date(r.decided_at).toLocaleString() : "—"}
                        </td>
                        <td className="px-3 py-2 text-xs italic text-zinc-700 max-w-xs">
                          {r.rejection_reason || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {rejecting && (
        <RejectSlugRequestModal
          request={rejecting}
          onClose={() => setRejecting(null)}
          onRejected={() => { setRejecting(null); load(); }}
        />
      )}
    </div>
  );
};

const RejectSlugRequestModal = ({ request, onClose, onRejected }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const r = reason.trim();
    if (!r) { toast.error("A rejection reason is required."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/partners/slug-requests/${request.id}/reject`,
        { reason: r }, { headers: authHdr() });
      toast.success("Request rejected.");
      onRejected();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rejection failed.");
    } finally { setBusy(false); }
  };
  return (
    <Modal title={`Reject slug change — ${request.partner_label || request.partner_id}`} onClose={onClose}>
      <p className="text-xs text-zinc-600 mb-3">
        Partner proposed{" "}
        <code className="font-mono text-indigo-700">{request.proposed_slug}</code>.
        The partner will see the rejection reason in their portal.
      </p>
      <Field
        label="Reason (required, visible to partner)"
        value={reason}
        onChange={setReason}
        testid="slug-request-reject-reason"
        multiline
      />
      <div className="flex justify-end gap-2 mt-3">
        <button onClick={onClose} className="px-3 py-1.5 text-sm" data-testid="slug-request-reject-cancel">Cancel</button>
        <button onClick={submit}
          disabled={busy || !reason.trim()}
          className="bg-rose-600 text-white px-3 py-1.5 text-sm disabled:opacity-50 inline-flex items-center gap-1"
          data-testid="slug-request-reject-confirm">
          {busy && <Loader2 className="animate-spin" size={12} />}
          Reject request
        </button>
      </div>
    </Modal>
  );
};
